import { Button } from 'react-bootstrap'

import CardHover from '@/components/CardHover/CardHover'
import Icon from '@/components/Icon/Icon'
import OutboundLink from '@/components/OutboundLink/OutboundLink'
import { CardType } from '@/types/card'
import { ComboWithVoteData } from '@/types/combo'
import { artCrop } from '@/utils/images'

import styles from './VoteInfo.module.scss'

const numberWithCommas = (x: number): string => x.toString().replace(/\B(?=(\d{3})+(?!\d))/g, ',')

type Props = {
  currentCombo: ComboWithVoteData
  previousCards: CardType[] | undefined
  previousCombo: ComboWithVoteData | undefined
}

const COMBO_VOTE_MONTH = '26_4'

const VoteInfo = ({ currentCombo, previousCards, previousCombo }: Props) => {
  const blueskyMessage = `I've voted ${currentCombo.userTotal} times in the EDHREC Combo Vote! Vote here: https://edhrec.com/combo-vote #combovote`

  const previousComboEarlyScore = previousCombo ? previousCombo[`comboEarlyScore_${COMBO_VOTE_MONTH}`] : undefined
  const previousComboPrereqScore = previousCombo ? previousCombo[`comboPrereqScore_${COMBO_VOTE_MONTH}`] : undefined
  const previousComboResultScore = previousCombo ? previousCombo[`comboResultScore_${COMBO_VOTE_MONTH}`] : undefined
  return (
    <div className={styles.infoContainer}>
      <div className={styles.cornerContainer}>
        {previousCards && previousCards.length == 2 && (
          <>
            <div className={styles.splitFirst} style={{ backgroundImage: `url(${artCrop(previousCards[0])})` }} />
            <div className={styles.splitSecond} style={{ backgroundImage: `url(${artCrop(previousCards[1])})` }} />
          </>
        )}
        {!previousCards?.length && (
          <div
            className={styles.split}
            style={{
              backgroundImage: `url(https://card-images.edhrec.com/art_crop/front/7/2/726e8b29-13e9-4138-b6a9-d2a0d8188d1c.jpg)`,
            }}
          />
        )}
      </div>

      <h3 className={styles.header}>Combo Vote</h3>
      <h4 className={styles.subHeader}>April 2026</h4>

      <div className='d-flex gap-4 m-3'>
        <div className='d-flex flex-column justify-content-center'>
          <strong className={styles.stat}>{numberWithCommas(currentCombo.total)}</strong>
          <label className={styles.label}>ALL VOTES</label>
        </div>
        <div className='d-flex flex-column justify-content-center'>
          <strong className={styles.stat}>{numberWithCommas(currentCombo.userTotal)}</strong>
          <label className={styles.label}>YOURS</label>
        </div>
      </div>

      <div className={styles.splitter} />
      {previousCombo && previousCards && (
        <div className='mt-2'>
          <h6 className={styles.previousSubHeader}>How other users voted on your last combo:</h6>
          <h5>
            {previousCards.map((card, i) => (
              <span key={i}>
                <CardHover card={card} />
                {i < previousCards.length - 1 && <span> | </span>}
              </span>
            ))}
          </h5>

          <div className={styles.chartsWrapper}>
            <div className='d-flex align-items-center gap-1'>
              <label className={styles.barLabel}>Early Game:</label>
              {previousComboEarlyScore !== undefined && (
                <>
                  <div className={styles.baseBar}>
                    <div className={styles.bar} style={{ width: `${previousComboEarlyScore * 100}%` }} />
                  </div>
                  <div className={styles.barPercent}>{Math.round(previousComboEarlyScore * 100)}%</div>
                </>
              )}
              {previousComboEarlyScore === undefined && <div>You voted first!</div>}
            </div>
            <div className='d-flex align-items-center gap-1'>
              <label className={styles.barLabel}>Trivial Prerequisites:</label>
              {previousComboPrereqScore !== undefined && (
                <>
                  <div className={styles.baseBar}>
                    <div className={styles.bar} style={{ width: `${previousComboPrereqScore * 100}%` }} />
                  </div>
                  <div className={styles.barPercent}>{Math.round(previousComboPrereqScore * 100)}%</div>
                </>
              )}
              {previousComboPrereqScore === undefined && <div>You voted first!</div>}
            </div>
            <div className='d-flex align-items-center gap-1'>
              <label className={styles.barLabel}>Significant Results:</label>
              {previousComboResultScore !== undefined && (
                <>
                  <div className={styles.baseBar}>
                    <div className={styles.bar} style={{ width: `${previousComboResultScore * 100}%` }} />
                  </div>
                  <div className={styles.barPercent}>{Math.round(previousComboResultScore * 100)}%</div>
                </>
              )}
              {previousComboResultScore === undefined && <div>You voted first!</div>}
            </div>
          </div>
        </div>
      )}
      <div className='d-flex justify-content-center'>
        <OutboundLink url={`https://bsky.app/intent/compose?text=${encodeURIComponent(blueskyMessage)}`}>
          <Button>
            <Icon icon='bluesky' /> Share on Bluesky
          </Button>
        </OutboundLink>
      </div>
    </div>
  )
}

export default VoteInfo
